"use client";
import cn from "classnames";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  getDataAutomationGroup,
  getDataAutomationId,
  getDataLinkAttributes,
} from "@/helpers";
import Link from "@/components/Link";
import Image from "next/image";
import { CaretDownSolid, Globe } from "@/icons";
import styles from "./SuperHeader.module.css";
import { GlobalNavigationProps } from "../GlobalNavigation";
import SkipToMainBtn from "../SkipToMainBtn";
import { getHrefFromCustomUrlPlugin, LOCALE_TO_ISO } from "@/helpers";
import SearchBar from "./SearchBar";
import { useState } from "react";
import { LOCALE_PREFERENCE_COOKIE_NAME } from "@/constants";
import { LocaleCode } from "@/types";

// Set cookie without using external library
const setLocaleCookie = (locale: LocaleCode) => {
  const maxAge = 365 * 24 * 60 * 60; // 1 year in seconds
  document.cookie = `${LOCALE_PREFERENCE_COOKIE_NAME}=${LOCALE_TO_ISO[locale]}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

export type SuperHeaderProps = Pick<
  GlobalNavigationProps,
  "superHeader" | "locale" | "asPath"
> & {};

/*
  Super header component is a top black navigation which consists of items, search input and language dropdown.
*/
const SuperHeader: React.FC<SuperHeaderProps> = ({
  superHeader,
  locale,
  asPath,
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const currentLanguage = superHeader?.languageSwitcher?.languages?.find(
    (lang) => lang.languageCode === locale,
  ) || {
    languageCode: "en-us",
    ariaLabel: "English",
  };
  const focusStyles =
    "data-[focus]:border-b-white data-[focus]:outline-swdc-blue-400 data-[focus]:outline-1 focus-visible:border-b-white focus-visible:outline-swdc-blue-400 focus-visible:outline-1";

  return (
    <div className="bg-swdc-gray-500 min-h-10 py-2">
      <nav
        className="swdc-container mx-auto flex h-full flex-row flex-wrap items-center justify-between gap-y-2"
        aria-label="Primary"
      >
        <SkipToMainBtn text={superHeader.skipToMainContentLabel} />
        <ul
          className={cn(
            "ml-6 flex flex-row items-center gap-x-6 whitespace-nowrap",
            styles.navList,
          )}
        >
          {!isSearchOpen &&
            superHeader?.items?.map((item, index) => {
              if (!!item.href) {
                return (
                  <li key={item.title}>
                    <Link
                      className={cn(
                        focusStyles,
                        "text-link-s active:text-swdc-orange-400 active:border-b-swdc-orange-400 inline-block border-y border-transparent text-white outline-none hover:border-b-white",
                        styles.navItem,
                      )}
                      href={getHrefFromCustomUrlPlugin(item.href)}
                      dld={item.dld}
                      dlt={item.dlt}
                      {...getDataAutomationId(`super-header-item-${index}`)}
                    >
                      {item.title}
                    </Link>
                  </li>
                );
              }
              return (
                <Popover
                  as="li"
                  className="relative flex items-center"
                  key={item.title}
                >
                  {({ open }) => (
                    <>
                      <PopoverButton
                        className={cn(
                          focusStyles,
                          "text-link-s flex flex-row items-center border-y border-transparent text-white outline-none hover:border-b-white",
                          styles.dropdownButton,
                          open && "border-b-white",
                        )}
                        {...getDataLinkAttributes({
                          dld: item.dld,
                          dlt: item.dlt,
                        })}
                        {...getDataAutomationId(
                          `super-header-item-${index}-dropdown`,
                        )}
                      >
                        <span className="flex-1">{item.title}</span>
                        <CaretDownSolid
                          className={cn(
                            "ml-2 w-3.5 shrink-0 text-white",
                            open ? "rotate-180" : "rotate-0",
                          )}
                          aria-hidden="true"
                          fill="currentColor"
                        />
                      </PopoverButton>
                      <PopoverPanel>
                        <div className="border-b-swdc-gray-500 absolute left-1/2 z-10 mt-6 -translate-x-1/2 border-x-8 border-b-8 border-solid border-x-transparent" />
                        <ul className="bg-swdc-gray-500 absolute -left-5 z-10 mt-8 min-w-37.5 space-y-2 rounded-lg p-5">
                          {item.links?.map(
                            ({ title, href, dld, dlt }) =>
                              href &&
                              title && (
                                <li key={title}>
                                  <PopoverButton
                                    as={Link}
                                    className={cn(
                                      focusStyles,
                                      "text-link-s active:border-b-swdc-orange-400 active:text-swdc-orange-400 inline-block border-y border-transparent whitespace-nowrap text-white outline-none hover:border-b-white",
                                      styles.dropdownItem,
                                    )}
                                    href={getHrefFromCustomUrlPlugin(href)}
                                    dld={dld}
                                    dlt={dlt}
                                  >
                                    {title}
                                  </PopoverButton>
                                </li>
                              ),
                          )}
                        </ul>
                      </PopoverPanel>
                    </>
                  )}
                </Popover>
              );
            })}
          {!isSearchOpen &&
            (superHeader.languageSwitcher?.languages?.length || 0) > 1 && (
              <Popover as="li" className="relative">
                <PopoverButton
                  aria-label={
                    superHeader?.languageSwitcher?.languageButtonLabel
                      ? superHeader?.languageSwitcher?.languageButtonLabel?.replace(
                          "{{lang}}",
                          currentLanguage.ariaLabel,
                        )
                      : `Current language ${currentLanguage.ariaLabel}, Change Language`
                  }
                  className={cn(
                    focusStyles,
                    "group flex items-center rounded-sm text-base text-white outline-none",
                  )}
                  {...getDataLinkAttributes({
                    dld: superHeader?.languageSwitcher?.languageButtonDLD,
                    dlt: superHeader?.languageSwitcher?.languageButtonDLT,
                  })}
                  {...getDataAutomationId("language-switcher")}
                >
                  <Globe className="size-5" color="#fff" />
                </PopoverButton>
                <PopoverPanel>
                  <div className="border-b-swdc-gray-500 absolute left-2.5 z-10 mt-3.5 -translate-x-1/2 border-x-8 border-b-8 border-solid border-x-transparent" />
                  <ul
                    className={cn(
                      "bg-swdc-gray-500 absolute z-10 mt-5.5 space-y-2 rounded-lg p-5",
                      styles.langDropdown,
                    )}
                    {...getDataAutomationGroup("panel")}
                  >
                    {superHeader?.languageSwitcher?.languages
                      ?.filter(
                        (lang) =>
                          lang.languageCode !== currentLanguage.languageCode,
                      )
                      ?.map(
                        ({
                          icon,
                          ariaLabel,
                          languageCode,
                          title,
                          href,
                          dld,
                          dlt,
                        }) => (
                          <li key={languageCode}>
                            <PopoverButton
                              aria-label={ariaLabel}
                              as={Link}
                              className={cn(
                                focusStyles,
                                "group flex items-center space-x-2 text-base whitespace-nowrap text-white outline-none",
                                styles.languageItem,
                              )}
                              href={`${languageCode === "en-us" ? "" : `/${href}`}${asPath}`}
                              locale={languageCode}
                              dld={dld}
                              dlt={dlt}
                              onClick={() =>
                                setLocaleCookie(languageCode as LocaleCode)
                              }
                            >
                              {icon && (
                                <Image
                                  src={icon}
                                  alt={ariaLabel + " icon"}
                                  height={20}
                                  width={20}
                                  aria-hidden={true}
                                  className="size-5 max-w-none rounded-2xl"
                                />
                              )}
                              <span
                                className="group-active:border-b-swdc-orange-400 group-active:text-swdc-orange-400 border-y border-transparent group-hover:border-b-white group-focus-visible:border-b-white"
                                lang={languageCode}
                              >
                                {title}
                              </span>
                            </PopoverButton>
                          </li>
                        ),
                      )}
                  </ul>
                </PopoverPanel>
              </Popover>
            )}
          {superHeader?.search && (
            <SearchBar
              search={superHeader.search}
              isSearchOpen={isSearchOpen}
              setIsSearchOpen={setIsSearchOpen}
            />
          )}
        </ul>
      </nav>
    </div>
  );
};

export default SuperHeader;
